import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { useExam } from "@/context/ExamContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API_BASE } from "@/App";
import { useTrackSection } from "@/hooks/useTrackSection";
import {
  BookOpen, Target, TrendingUp, Zap, Clock, BarChart3,
  FileText, Brain, Calendar, AlertCircle, CheckCircle2,
  Lock, ArrowRight, Award, ChevronRight, ArrowLeft,
  Newspaper, PenTool, Lightbulb, FlaskConical, Flame,
  Trophy, Star, Rocket, Shield, Play, GraduationCap,
  BookMarked, Sparkles, TrendingDown, Activity, Dna
} from "lucide-react";

/**
 * CLARIAS Prelims Hub - SuperKalam Inspired Vibrant Design
 * Central hub for all Prelims preparation features
 */

const PrelimsHubPage = () => {
  const { user } = useAuth();
  const { isSI, isPro } = useExam();  const navigate = useNavigate();
  useTrackSection(); // Track section visit
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  // Exam countdown — Next UPSC Prelims with auto-rollover (advances year-by-year if past)
  const getNextPrelimsDate = () => {
    const exam = new Date('2027-05-23'); // UPSC Prelims 2027
    exam.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    while (exam < today) {
      exam.setFullYear(exam.getFullYear() + 1);
    }
    return exam;
  };
  const PRELIMS_DATE = getNextPrelimsDate();
  const getDaysRemaining = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.ceil((PRELIMS_DATE - today) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };
  const daysToExam = getDaysRemaining();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/prelims/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Track activity when clicking on features
  const handleFeatureClick = async (feature) => {
    try {
      const token = localStorage.getItem("token");
      await managedAxios.post(
        `${API_BASE}/command/track-activity?activity_type=prelims&activity_name=${encodeURIComponent(feature.title)}&activity_path=${feature.path}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // Silently fail - tracking is non-critical
    }
    navigate(feature.path);
  };

  // Hero features - most important
  const heroFeatures = [
    { 
      title: "Practice Hub", 
      description: "20 MCQs on any chapter or subtopic · evergreen + fresh Current Affairs", 
      icon: BookMarked, 
      path: "/prelims/chapter-practice", 
      color: "from-emerald-500 to-teal-600",
      stats: "Search any topic →",
      highlight: true
    },
    { 
      title: "CSAT Suite", 
      description: "Comprehension, Logic, Math & Decision Making", 
      icon: Shield, 
      path: "/csat-suite", 
      color: "from-cyan-500 to-teal-600",
      stats: `${stats?.csat_accuracy || 0}% accuracy`
    },
  ];

  // Practice features
  const practiceFeatures = [
    ...(isSI ? [{ title: "SI PYQ Papers", description: "Previous year SI exam papers (TS/AP) with answer keys", icon: BookMarked, path: "/si/pyq", color: "from-emerald-500 to-teal-600", proOnly: true }] : []),
    ...(!isSI ? [{ title: "PYQ Papers", description: "Year-wise previous papers (2013-2024)", icon: BookMarked, path: "/prelims/pyq-papers", color: "from-amber-500 to-orange-500", proOnly: false }] : []),
    { title: "Mistake Tracker", description: "Track & revise your wrong answers", icon: AlertCircle, path: "/mistake-tracker", color: "from-orange-500 to-red-500", highlight: true },
  ];

  // Analytics features. SI aspirants get a single, curated Topic Heatmap
  // (GS importance matrix for TS/AP); the UPSC-specific tools are hidden.
  const analyticsFeatures = isSI
    ? [
        { title: "Topic Heatmap", description: "GS importance matrix — TS & AP", icon: BarChart3, path: "/prelims/heatmap", color: "from-blue-500 to-indigo-500" },
      ]
    : [
        { title: "PYQ Pattern Genome", description: "Topic rotation tracker - predict 2026 questions", icon: Dna, path: "/prelims/genome", color: "from-purple-500 to-pink-500", highlight: true },
        { title: "PYQ Analyzer", description: "30-year trend analysis & predictions", icon: TrendingUp, path: "/prelims/pyq-analyzer", color: "from-emerald-500 to-green-500" },
        { title: "Topic Heatmap", description: "Importance matrix for all subjects", icon: BarChart3, path: "/prelims/heatmap", color: "from-blue-500 to-indigo-500" },
        { title: "Pattern Prediction", description: "AI-predicted high-probability topics", icon: Lightbulb, path: "/prelims/pattern-prediction", color: "from-violet-500 to-purple-500", proOnly: true },
        { title: "Weak Area Analytics", description: "Identify gaps & improvement plan", icon: AlertCircle, path: "/prelims/analytics", color: "from-orange-500 to-red-500", proOnly: true },
      ];

  // CA to Prelims features
  const caFeatures = [
    { title: "CA → Prelims Converter", description: "Convert news to MCQ practice", icon: Newspaper, path: "/prelims/ca-converter", color: "from-sky-500 to-blue-500", proOnly: true },
    { title: "Newspaper Converter", description: "Editorial to prelims questions", icon: FileText, path: "/prelims/newspaper-converter", color: "from-teal-500 to-cyan-500", proOnly: true },
  ];

  // Tools
  const toolFeatures = [
    { title: "Trap Detector", description: "Identify UPSC traps in MCQs", icon: AlertCircle, path: "/prelims/trap-detector", color: "from-red-500 to-orange-500", proOnly: true },
    { title: "Revision Scheduler", description: "Spaced repetition for retention", icon: Calendar, path: "/prelims/revision", color: "from-indigo-500 to-violet-500" },
  ];

  const getStatusColor = (status) => {
    if (status === "safe") return "from-emerald-500 to-green-500";
    if (status === "borderline") return "from-amber-500 to-orange-500";
    return "from-red-500 to-rose-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-indigo-300">Loading Prelims Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/30">
      
      {/* ═══════════════════════════════════════════════════════════
          VIBRANT HEADER
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Back button & Title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate("/dashboard")}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
                  <Target className="w-7 h-7" />
                  Prelims Hub
                </h1>
                <p className="text-indigo-200 text-sm">Master the art of MCQs</p>
              </div>
            </div>
            
            {/* Days Counter */}
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold leading-none">{daysToExam}</p>
              <p className="text-[10px] text-indigo-200">days to Prelims</p>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.total_mcqs_attempted || 0}</p>
              <p className="text-[10px] text-indigo-200">MCQs Done</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.overall_accuracy || 0}%</p>
              <p className="text-[10px] text-indigo-200">Accuracy</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.tests_taken || 0}</p>
              <p className="text-[10px] text-indigo-200">Tests</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/30 to-orange-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{weeksToExam}</p>
              <p className="text-[10px] text-amber-200">Weeks Left</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* ═══════════════════════════════════════════════════════════
            GS STATUS CARD
            ═══════════════════════════════════════════════════════════ */}
        <Card className={`border-0 shadow-lg overflow-hidden bg-gradient-to-r ${getStatusColor(stats?.gs_status || 'safe')}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  {stats?.gs_status === "safe" ? (
                    <CheckCircle2 className="w-7 h-7" />
                  ) : stats?.gs_status === "borderline" ? (
                    <AlertCircle className="w-7 h-7" />
                  ) : (
                    <AlertCircle className="w-7 h-7" />
                  )}
                </div>
                <div>
                  <p className="text-white/80 text-xs uppercase tracking-wide">GS Prelims Status</p>
                  <p className="text-2xl font-bold font-heading">
                    {stats?.gs_status === "safe" ? "🟢 SAFE ZONE" :
                     stats?.gs_status === "borderline" ? "🟡 BORDERLINE" :
                     "🔴 NEEDS ATTENTION"}
                  </p>
                  <p className="text-white/80 text-sm">{stats?.gs_accuracy || 0}% current accuracy</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/prelims/chapter-practice")}
                className="bg-white text-slate-800 hover:bg-slate-100 font-semibold"
                data-testid="gs-status-practice-cta"
              >
                <Play className="w-4 h-4 mr-2" />
                Practice Now
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            MASTER MATERIAL SPOTLIGHT — All 8 GS subjects
            ═══════════════════════════════════════════════════════════ */}
        <Card
          className="border-0 shadow-xl cursor-pointer hover:scale-[1.01] transition-all overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600"
          data-testid="master-material-tile"
          onClick={() => navigate("/prelims/master")}
        >
          <CardContent className="p-5 sm:p-7 text-white">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-white/25 text-white border-0 text-[10px] uppercase tracking-wider">New</Badge>
                  <Badge className="bg-amber-400/90 text-amber-900 border-0 text-[10px]">Premium Notes</Badge>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-heading mb-1">
                  📚 Master Material
                </h3>
                <p className="text-white/85 text-sm">
                  Chapter-wise study material across all 8 GS subjects with PYQ map, traps, mind maps & 5 MCQs each. Live current-affairs strip on every chapter.
                </p>
                <div className="flex flex-wrap gap-3 mt-3 text-xs text-white/90">
                  <span>• 8 subjects</span>
                  <span>• Topper-grade</span>
                  <span>• Auto-fresh CA</span>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-white/70 flex-shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            HERO FEATURES
            ═══════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 gap-4">
          {heroFeatures.map((feature, idx) => (
            <Card 
              key={idx}
              className={`border-0 shadow-xl cursor-pointer hover:scale-[1.02] transition-all overflow-hidden bg-gradient-to-br ${feature.color}`}
              onClick={() => handleFeatureClick(feature)}
            >
              <CardContent className="p-5 text-white">
                <div className="flex flex-col h-full">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-3">
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold font-heading text-lg mb-1">{feature.title}</h3>
                  <p className="text-white/80 text-sm mb-3 flex-1">{feature.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-white/20 text-white border-0 text-xs">
                      {feature.stats}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-white/60" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            PRACTICE SECTION
            ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Flame className="w-5 h-5 text-orange-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Practice & Tests</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {practiceFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-sm text-slate-800">{feature.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{feature.description}</p>
                    </div>
                    {feature.proOnly && !isPro && (
                      <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            ANALYTICS SECTION
            ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Analytics & Insights</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {analyticsFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className={`border cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden ${
                  feature.highlight 
                    ? "border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 ring-2 ring-purple-200" 
                    : "border-slate-200 bg-white"
                }`}
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4 text-center relative">
                  {feature.highlight && (
                    <Badge className="absolute top-2 right-2 bg-purple-600 text-white border-0 text-[9px] px-1.5 py-0.5">
                      NEW
                    </Badge>
                  )}
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{feature.description}</p>
                  {feature.proOnly && !isPro && (
                    <Badge className="mt-2 bg-amber-100 text-amber-700 border-0 text-[10px]">
                      <Lock className="w-3 h-3 mr-1" />
                      PRO
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            CA TO PRELIMS SECTION (PRO ONLY) — hidden for SI track
            ═══════════════════════════════════════════════════════════ */}
        {!isSI && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Newspaper className="w-5 h-5 text-sky-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Current Affairs → Prelims</h2>
            <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">PRO</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {caFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-md`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-slate-800">{feature.title}</p>
                      <p className="text-xs text-slate-500">{feature.description}</p>
                    </div>
                    {!isPro ? (
                      <Lock className="w-5 h-5 text-amber-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            TOOLS SECTION — hidden for SI track
            ═══════════════════════════════════════════════════════════ */}
        {!isSI && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Smart Tools</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {toolFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{feature.description}</p>
                  {feature.proOnly && !isPro && (
                    <Badge className="mt-2 bg-amber-100 text-amber-700 border-0 text-[10px]">
                      <Lock className="w-3 h-3 mr-1" />
                      PRO
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        )}

        {/* ═══════════════════════════════════════════════════════════
            PRO UPGRADE CTA (for FREE users)
            ═══════════════════════════════════════════════════════════ */}
        {!isPro && (
          <Card 
            className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/upgrade")}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                  <p className="text-sm text-slate-600">Unlock all Prelims features & more</p>
                </div>
                <ChevronRight className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Spacing */}
        <div className="h-8" />
      </div>
    </div>
  );
};

export default PrelimsHubPage;
